import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import { z } from "zod";

const SAS_TOKEN_LEGACY = "SAS_TOKEN";
const SAS_TOKEN_EXPIRES_ON_LEGACY = "SAS_TOKEN_EXPIRES_ON";

const sasTokenKey = (containerName) => `SAS_TOKEN_${containerName}`;
const sasTokenExpiresOnKey = (containerName) =>
  `SAS_TOKEN_EXPIRES_ON_${containerName}`;

const containerName = "documents";

export const getFileIcon = (file) => {
  if (file?.type?.includes?.("pdf")) return "pi pi-file-pdf";
  else if (file?.type?.includes?.("msword") || file?.type?.includes?.("word"))
    return "pi pi-file-word";
  else if (
    file?.type?.includes?.("png") ||
    file?.type?.includes?.("jpg") ||
    file?.type?.includes?.("jpeg")
  )
    return "pi pi-image";
  else if (
    file?.type?.includes?.("csv") ||
    file?.type?.includes?.("excel") ||
    file?.type?.includes?.("spreadsheet")
  )
    return "pi pi-file-excel";
  else return "pi pi-file";
};

export const downloadFileHandler = async (
  file,
  containerName = "documents",
) => {
  try {
    const rawBlobUrl = file.blob_url;
    const rawBlobFileName = file.blobName
      ? file.blobName.split("/").pop()
      : null;
    // Only use blobName when it's a human-readable name, not a UUID-based path
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const blobFileName =
      rawBlobFileName && !uuidPattern.test(rawBlobFileName)
        ? rawBlobFileName
        : null;
    const rawOriginal =
      blobFileName ||
      file.metadata?.original_file_name ||
      file.original_file_name ||
      file.metadata?.file_name ||
      file.file_name ||
      file.fileName;
    const fileName = rawOriginal ? decodeURIComponent(rawOriginal) : file.name;

    let downloadUrl;
    if (rawBlobUrl && rawBlobUrl.startsWith("blob:")) {
      // Local browser blob URL (file not yet uploaded to storage) — use directly
      downloadUrl = rawBlobUrl;
    } else {
      const blobUrl = rawBlobUrl.split("?")[0];
      downloadUrl = await fetchBlobUrl(blobUrl, containerName);
    }

    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading the file:", error);
  }
};
export const getDocumentLabel = (filename) => {
  if (filename == null || typeof filename !== "string") return "Other Document";
  const rawExt = filename.split(".").pop() || "";
  const extension = rawExt
    .replace(/\s*\([^)]*\)$/, "")
    .trim()
    .toLowerCase();
  const extensionMap = {
    jpg: "Image Document",
    jpeg: "Image Document",
    png: "Image Document",
    pdf: "PDF Document",
    doc: "Word Document",
    docx: "Word Document",
    xls: "Excel Document",
    xlsx: "Excel Document",
    ppt: "PPT Document",
    pptx: "PPT Document",
    txt: "Text Document",
    csv: "CSV Document",
  };
  return extensionMap[extension] || "Other Document";
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "kb", "mb", "gb", "tb"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const getPeriodFromFileYearMonth = (fileYearMonth) => {
  if (
    !fileYearMonth ||
    typeof fileYearMonth.year !== "number" ||
    typeof fileYearMonth.month !== "number"
  )
    return undefined;
  return `${fileYearMonth.year}-${String(fileYearMonth.month).padStart(2, "0")}`;
};

/**
 * @param {string} containerName
 * @param {{ forceRefresh?: boolean, entityId?: string, serviceLineCode?: string, fileName?: string }} [options]
 * @returns {Promise<string>}
 */
export const getSasToken = async (containerName, options = {}) => {
  const { entityId, serviceLineCode, fileName, ...restOptions } = options;
  const hasScopeParams = entityId && serviceLineCode && fileName;

  const key = sasTokenKey(containerName);
  const expiresKey = sasTokenExpiresOnKey(containerName);

  const isTokenValid = (token, expiresOn) => {
    if (!token || !expiresOn) return false;
    const now = new Date();
    const expiry = new Date(expiresOn);
    const bufferMs = 60 * 1000;
    return now.getTime() + bufferMs < expiry.getTime();
  };

  const saveToken = (token, expiresOn) => {
    sessionStorage.setItem(key, token);
    sessionStorage.setItem(expiresKey, expiresOn);
  };

  // Skip cache for scoped tokens (they are file-specific)
  if (!restOptions.forceRefresh && !hasScopeParams) {
    let sasToken = sessionStorage.getItem(key);
    let expiresOn = sessionStorage.getItem(expiresKey);
    if (!sasToken || !expiresOn) {
      sasToken = sessionStorage.getItem(SAS_TOKEN_LEGACY);
      expiresOn = sessionStorage.getItem(SAS_TOKEN_EXPIRES_ON_LEGACY);
      if (sasToken && expiresOn) saveToken(sasToken, expiresOn);
    }
    if (isTokenValid(sasToken, expiresOn)) {
      return sasToken;
    }
  }

  const scopeParams = hasScopeParams
    ? { entityId, serviceLineCode, fileName }
    : {};
  const newTemporaryToken = await fetchTemporarySasToken(
    containerName,
    scopeParams,
  );

  // Only cache non-scoped tokens
  if (!hasScopeParams) {
    saveToken(newTemporaryToken.sasToken, newTemporaryToken.expiresOn);
  }
  return newTemporaryToken.sasToken;
};

/**
 * Get a scoped SAS token for document uploads.
 * Returns the full server response including server-generated blobName and blobUrl.
 * @param {string} containerName
 * @param {{ entityId: string, serviceLineCode: string, fileName: string }} scopeParams
 * @returns {Promise<{ containerName: string, blobName: string, documentUuid: string, sasToken: string, expiresOn: string, blobUrl: string }>}
 */
export const getScopedUploadToken = async (containerName, scopeParams) => {
  const tokenData = await fetchTemporarySasToken(containerName, scopeParams);
  return tokenData;
};

const fetchTemporarySasToken = async (containerName, scopeParams = {}) => {
  try {
    let url = API_ROUTES.FETCH_TEMPORARY_TOKEN;

    // Append scoped query params for containers that require them (e.g. 'documents')
    const queryParts = [];
    if (scopeParams.entityId)
      queryParts.push(`entityId=${encodeURIComponent(scopeParams.entityId)}`);
    if (scopeParams.serviceLineCode)
      queryParts.push(
        `serviceLineCode=${encodeURIComponent(scopeParams.serviceLineCode)}`,
      );
    if (scopeParams.fileName)
      queryParts.push(`fileName=${encodeURIComponent(scopeParams.fileName)}`);
    if (queryParts.length > 0) {
      url = `${url}?${queryParts.join("&")}`;
    }

    const data = await apiService(url, "GET", {
      replace: { container: containerName },
    });
    return data?.data;
  } catch (err) {
    console.log(`An unexpected error occured while fetching the token: ${err}`);
  }
};

export const openFileInNewTab = async ({ objectURL }) => {
  try {
    const response = await fetch(objectURL);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (err) {
    console.error("Error opening preview:", err);
  }
};

export const openPreviewInNewTab = async (
  file,
  containerName = "documents",
) => {
  try {
    const blobUrl = file.objectURL.split("?")[0];
    const downloadUrl = await fetchBlobUrl(blobUrl, containerName);
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (error) {
    console.error("Error opening preview:", error);
  }
};

export const removeToken = () => {
  sessionStorage.removeItem(SAS_TOKEN_LEGACY);
  sessionStorage.removeItem(SAS_TOKEN_EXPIRES_ON_LEGACY);
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith("SAS_TOKEN_") || k?.startsWith("SAS_TOKEN_EXPIRES_ON_")) {
      keysToRemove.push(k);
    }
  }
  keysToRemove.forEach((k) => sessionStorage.removeItem(k));
};

const fetchBlobUrl = async (blobUrl, containerName) => {
  try {
    const data = await apiService(API_ROUTES.FETCH_BLOB_URL, "GET", {
      replace: { blobUrl, containerName },
      schema: z.object({
        blobUrl: z.string(),
        containerName: z.string(),
      }),
    });
    return data?.data?.baseUrl;
  } catch (err) {
    console.log(
      "An unexpected error occurred whiled fetching blob url ",
      err.message,
    );
    throw err;
  }
};

export const getFilenameFromReference = (fileReference) => {
  if (!fileReference) return "";
  try {
    const url = new URL(fileReference);
    const pathname = url.pathname;
    const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
    return decodeURIComponent(filename);
  } catch (error) {
    console.error("Error extracting filename from reference:", error);
    return "";
  }
};
export const PayslipStatusMap = {
  pending: "Processing",
  rejected: "Rejected",
  approved: "Approved",
  released: "Released",
  matched: "Awaiting Review",
  unmatched: "Awaiting Review",
  no_match: "Awaiting Review",
  extraction_failed: "Awaiting Review",
};

export const getPayslipBlobName = (fileName) => {
  return fileName.split("/").pop();
};

export const getAdHocEscalationDisplayName = (
  subCategoryName,
  date,
  fileName,
) => {
  try {
    if (!subCategoryName || !fileName) return fileName;
    const extension = fileName.split(".").pop().toLowerCase();
    if (!date) {
      return extension ? `${subCategoryName}.${extension}` : subCategoryName;
    }
    const month = new Date(date).toLocaleString("default", { month: "long" });
    const year = new Date(date).getFullYear();
    return `${subCategoryName} - ${month} ${year}.${extension}`;
  } catch (error) {
    console.error("Error generating display name:", error);
    return fileName;
  }
};

export const formatVersionedAttachmentDisplayName = (
  subCategoryName,
  periodMonthYear,
  versionNumber,
  extension,
) => {
  const ext = (extension || "").toLowerCase();
  const extPart = ext ? `.${ext}` : "";
  const n = Math.max(1, Math.floor(Number(versionNumber)) || 1);
  if (!subCategoryName) return ext ? `File ${n}.${ext}` : `File ${n}`;
  if (periodMonthYear) {
    return `${subCategoryName} - ${periodMonthYear} ${n}${extPart}`;
  }
  return `${subCategoryName} ${n}${extPart}`;
};

/** Returns the original/backend file name for display when subcategory naming is out of scope (e.g. Employee Registry, Payroll). */
export const getOriginalFileName = (file) => {
  if (!file) return "";
  const raw =
    file?.metadata?.original_file_name ||
    file?.original_file_name ||
    file?.name ||
    "";
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/** Returns the backend storage/display name (attachmentData.name from API) for Payroll service line. */
export const getBackendFileName = (file) => {
  if (!file) return "";
  const raw = file?.name || "";
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * Returns a stable key for version grouping: same subcategory (and period when present)
 * so that version numbers can be computed consistently when sending and when displaying.
 * Used by conversation versioning to count existing attachments with the same key.
 * @param {object} fileObj - File or attachment (upload object or API attachmentData item)
 * @returns {string} Key like "categoryCode_subCategoryCode_YYYY-MM" or "categoryCode_subCategoryCode_"
 */
export const getAttachmentVersionKey = (fileObj) => {
  if (!fileObj) return "";
  const categoryCode =
    typeof fileObj?.category === "object"
      ? fileObj.category?.code || fileObj.category?.name
      : (fileObj?.category ?? "");
  const subCategoryCode =
    typeof fileObj?.subcategory === "object"
      ? fileObj.subcategory?.code || fileObj.subcategory?.name
      : (fileObj?.subcategory ??
        fileObj?.subCategory ??
        fileObj?.subCategoryName ??
        fileObj?.subcategoryName ??
        (typeof fileObj?.metadata?.subcategory === "object"
          ? fileObj.metadata?.subcategory?.code ||
            fileObj.metadata?.subcategory?.name
          : (fileObj?.metadata?.subcategory ??
            fileObj?.metadata?.subCategory ??
            "")));
  const period =
    getPeriodFromFileYearMonth(fileObj?.fileYearMonth) ||
    fileObj?.period ||
    fileObj?.metadata?.period ||
    "";
  return `${categoryCode}_${subCategoryCode}_${period}`;
};

export const resolveDisplayFileName = (
  file,
  allFilesInScope = [],
  getSubCategoryNameFn,
) => {
  if (!file) return "";

  const decodeSafe = (value) => {
    if (!value) return "";
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const splitNameAndExtension = (filename = "") => {
    const lastDot = filename.lastIndexOf(".");
    if (lastDot <= 0) {
      return { base: filename, extension: "" };
    }
    return {
      base: filename.slice(0, lastDot),
      extension: filename.slice(lastDot + 1).toLowerCase(),
    };
  };

  const normalizeCategoryCode = (categoryValue) => {
    if (!categoryValue) return undefined;
    if (typeof categoryValue === "object") {
      return categoryValue?.code || categoryValue?.name;
    }
    return categoryValue;
  };

  const normalizeSubCategoryValue = (fileObj) => {
    const rawSubCategory =
      fileObj?.subCategory ??
      fileObj?.subCategoryName ??
      fileObj?.sub_category ??
      fileObj?.subcategoryName ??
      fileObj?.subcategory ??
      fileObj?.metadata?.subCategory ??
      fileObj?.metadata?.sub_category ??
      fileObj?.metadata?.subcategory;

    if (rawSubCategory && typeof rawSubCategory === "object") {
      return {
        code: rawSubCategory?.code,
        name: rawSubCategory?.name,
        value: rawSubCategory?.code || rawSubCategory?.name,
      };
    }

    return {
      code: rawSubCategory,
      name: rawSubCategory,
      value: rawSubCategory,
    };
  };

  const getSubCategoryLabel = (fileObj) => {
    const { code, name, value } = normalizeSubCategoryValue(fileObj);
    const categoryCode = normalizeCategoryCode(fileObj?.category);

    if (typeof getSubCategoryNameFn === "function") {
      const resolved = getSubCategoryNameFn(categoryCode, code || value);
      if (resolved && resolved !== "-") return resolved;
    }

    return name || code || value;
  };

  const toBaseName = (fileObj) => {
    const rawSubCategoryName = getSubCategoryLabel(fileObj);
    if (!rawSubCategoryName) return "";

    const subCategoryName = rawSubCategoryName.includes("_")
      ? rawSubCategoryName
          .toLowerCase()
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : rawSubCategoryName;

    const period = fileObj?.period || fileObj?.metadata?.period || null;

    const explicitYear =
      fileObj?.year ??
      fileObj?.fileYear ??
      fileObj?.fileYearMonth?.year ??
      fileObj?.metadata?.year ??
      fileObj?.metadata?.fileYearMonth?.year ??
      null;

    const explicitMonth =
      fileObj?.month ??
      fileObj?.fileMonth ??
      fileObj?.fileYearMonth?.month ??
      fileObj?.metadata?.month ??
      fileObj?.metadata?.fileYearMonth?.month ??
      null;

    let monthYear = "";

    if (explicitYear && explicitMonth) {
      const date = new Date(Number(explicitYear), Number(explicitMonth) - 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      monthYear = `${monthName} ${explicitYear}`;
    } else if (period && period.includes("-")) {
      const [year, month] = period.split("-");
      const date = new Date(Number(year), Number(month) - 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      monthYear = `${monthName} ${year}`;
    }

    return monthYear ? `${subCategoryName} - ${monthYear}` : subCategoryName;
  };

  const originalName =
    file?.metadata?.original_file_name ||
    file?.original_file_name ||
    file?.name ||
    "";

  const metadataDisplayName = decodeSafe(
    file?.metadata?.display_name || file?.display_name,
  );
  const fallbackName = metadataDisplayName || decodeSafe(originalName);
  const fallbackParts = splitNameAndExtension(fallbackName);

  let baseName = toBaseName(file) || fallbackParts.base;
  const extension = fallbackParts.extension;

  if (!baseName) return fallbackName;

  const getComparableBaseName = (f) => {
    const derivedBase = toBaseName(f);
    if (derivedBase) return derivedBase;

    const rawName = decodeSafe(
      f?.metadata?.display_name ||
        f?.display_name ||
        f?.metadata?.original_file_name ||
        f?.original_file_name ||
        f?.name,
    );

    return splitNameAndExtension(rawName).base;
  };

  if (allFilesInScope?.length) {
    const sameBaseFiles = allFilesInScope.filter((f) => {
      return getComparableBaseName(f) === baseName;
    });

    if (sameBaseFiles.length) {
      const currentIndex = sameBaseFiles.findIndex((f) => {
        if (f?.blob_url && file?.blob_url) return f.blob_url === file.blob_url;
        return (
          (f?.name || f?.original_file_name) ===
          (file?.name || file?.original_file_name)
        );
      });

      const index = currentIndex >= 0 ? currentIndex + 1 : sameBaseFiles.length;

      baseName = `${baseName} ${index}`;
    }
  }

  return extension ? `${baseName}.${extension}` : baseName;
};
